import {memo} from 'react';

import styled from 'styled-components';

import {ActionButton} from '~/shared/components/Button';
import FormField from '~/shared/components/FormField';
import {flipOnLTR} from '~/shared/theme/utils';
import {body16Bold} from '~/shared/theme/typography';
import {getLocalizationService} from '~/shared/services/localisationService';

import {ButtonBar, FormContainer, AddressDropdownButton} from '../../styled.addressDropdown';

import {getFields} from './fields';
import CommentsField from './CommentsField';

const SaveChangesActionButton = styled(ActionButton).attrs({
  'data-test-id': 'updateAddressBtn',
})`
  width: ${p => (p.full ? '100%' : 'inherit')};
  margin: inherit;
`;

const LinkButton = styled(AddressDropdownButton).attrs({
  'data-test-id': 'deleteAddressBtn',
})`
  background-color: transparent;
  color: ${({theme}) => theme.colors.error};
  text-decoration-line: underline;
  ${body16Bold};
`;

const Fields = styled.div`
  display: flex;
  flex-wrap: no-wrap;

  & > div:not(:first-child) {
    ${flipOnLTR`
      margin-right: 17px;
    `}
  }
`;

const Form = styled.form`
  flex: 1;
  overflow: auto;
`;

const Scrollable = styled.div`
  background-color: ${({backgroundColor, theme}) => backgroundColor || theme.addressDropdown.form.backgroundColor};
  overflow-y: auto;
  height: ${({editing}) => (editing ? 405 : 460)}px;
`;

const FormRender = ({
  handleSubmit,
  invalid,
  values,
  isCommentsExpended,
  setIsCommentsExpended,
  isCompanyAddress,
  disabledDelete,
  cityNameAutocomplete,
  handleDeleteButtonClick,
  getStreetNameAutocomplete,
  fetchCityNamesIfNeeded,
  fetchStreetNamesIfNeeded,
  streetNameAutocomplete,
  backgroundColor,
  submitFormButtonText,
  form,
  editing,
  loading,
}) => {
  const {t} = getLocalizationService();

  const fields = getFields({
    values,
    form,
    isCompanyAddress,
    cityNameAutocomplete,
    getStreetNameAutocomplete,
    fetchCityNamesIfNeeded,
    fetchStreetNamesIfNeeded,
    streetNameAutocomplete,
    t,
  });

  return (
    <Form onSubmit={handleSubmit}>
      <Scrollable {...{editing, backgroundColor}}>
        <FormContainer>
          <>
            <FormField name="cityId" hidden />
            <FormField name="streetId" hidden />
            <FormField name="addressId" hidden />
            <FormField name="isCompanyAddress" hidden />

            {fields.map(labelledFields => (
              <Fields key={labelledFields[0].name}>
                {labelledFields.map(
                  ({name, hide, ...rest}) =>
                    !hide && (
                      <FormField
                        {...{
                          key: name,
                          name,
                          ...rest,
                        }}
                      />
                    ),
                )}
              </Fields>
            ))}

            <CommentsField
              {...{
                isCommentsExpended,
                setIsCommentsExpended,
              }}
            />
          </>
        </FormContainer>
      </Scrollable>

      <ButtonBar center={isCompanyAddress || disabledDelete}>
        {!isCompanyAddress && !disabledDelete && (
          <LinkButton onClick={handleDeleteButtonClick}>{t('delete_address')}</LinkButton>
        )}
        <SaveChangesActionButton
          full={!(!isCompanyAddress && !disabledDelete)}
          isDisabled={invalid || loading}
          type="submit"
        >
          {submitFormButtonText || t('save_changes')}
        </SaveChangesActionButton>
      </ButtonBar>
    </Form>
  );
};

export default memo(FormRender);
